'use client'

import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPost } from '@/lib/api'
import { message } from '@/components/Toast'
import { dialog } from '@/components/StandardDialog'
import type { LocalWechatAccount } from '@/types'

type LocalWechatAccountsOptions = {
  consoleUserUuid: string
  /** wxid of the wechat account currently selected in the shell. */
  currentWxid: string
  /** Drop the local wechat session — the selected account is no longer usable. */
  onCurrentAccountCleared: () => void
  /** Send the user to the QR page: the stored session cannot be woken up again. */
  onSessionExpired: () => void
}

type LocalWechatAccountsState = {
  localAccounts: LocalWechatAccount[]
  /** Turns true after the first /accounts attempt, success or failure. */
  localAccountsLoaded: boolean
  localAccountsError: string
  refreshLocalAccounts: () => Promise<void>
  /** Toasts and returns false when the wxid is not bound to this console user. */
  assertOwnWechatAccount: (wxid: string) => boolean
  onlineWxAccount: (wxid: string) => Promise<void>
  downlineWxAccount: (wxid: string) => Promise<void>
  removeWxAccount: (wxid: string) => Promise<void>
}

function reason(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

export function useLocalWechatAccounts({
  consoleUserUuid,
  currentWxid,
  onCurrentAccountCleared,
  onSessionExpired,
}: LocalWechatAccountsOptions): LocalWechatAccountsState {
  const [localAccounts, setLocalAccounts] = useState<LocalWechatAccount[]>([])
  const [localAccountsLoaded, setLocalAccountsLoaded] = useState(false)
  const [localAccountsError, setLocalAccountsError] = useState('')

  const refreshLocalAccounts = useCallback(async () => {
    try {
      const response = await apiGet<{ success: boolean; accounts: LocalWechatAccount[] }>('/accounts', {
        silent: true,
      })
      setLocalAccounts(response.accounts || [])
      setLocalAccountsError('')
    } catch (error) {
      setLocalAccountsError(reason(error))
      message.error(`微信账号列表加载失败：${reason(error)}`)
    } finally {
      // Must be set even on failure: the "restore last selection" effect is
      // gated on it, so leaving it false pins the shell to "未选择微信".
      setLocalAccountsLoaded(true)
    }
  }, [])

  useEffect(() => {
    void refreshLocalAccounts()
  }, [refreshLocalAccounts, currentWxid])

  const isOwnWechatAccount = useCallback(
    (wxid: string) => localAccounts.some((item) => item.wxid === wxid && item.localUserUuid === consoleUserUuid),
    [consoleUserUuid, localAccounts],
  )

  const assertOwnWechatAccount = useCallback(
    (wxid: string): boolean => {
      if (isOwnWechatAccount(wxid)) return true
      message.error('只能操作当前控制台账号绑定的微信号')
      return false
    },
    [isOwnWechatAccount],
  )

  async function onlineWxAccount(wxid: string) {
    if (!assertOwnWechatAccount(wxid)) return
    if (
      !(await dialog.confirm({
        title: '确认上线该微信账号？',
        message: `wxid：${wxid}\n\n此操作会尝试使用本地会话执行二次登录；如会话已失效，请重新扫码登录。`,
      }))
    )
      return
    try {
      await apiPost(`/accounts/${encodeURIComponent(wxid)}/online`, {}, { silent: true })
      await refreshLocalAccounts()
    } catch (error) {
      message.error(`上线失败，请重新扫码登录：${reason(error)}`)
      onSessionExpired()
    }
  }

  async function downlineWxAccount(wxid: string) {
    if (!assertOwnWechatAccount(wxid)) return
    if (
      !(await dialog.confirm({
        title: '确认下线该微信账号？',
        message: `wxid：${wxid}\n\n此操作只请求协议退出，不会从当前控制台列表移除。`,
      }))
    )
      return
    let succeeded = false
    try {
      await apiPost(`/accounts/${encodeURIComponent(wxid)}/downline`, {}, { silent: true })
      succeeded = true
    } catch (error) {
      message.error(`下线失败：${reason(error)}`)
    }
    await refreshLocalAccounts()
    // Only a confirmed protocol logout may clear the local session; otherwise
    // the shell would claim the account is gone while it is still online.
    if (succeeded && currentWxid === wxid) onCurrentAccountCleared()
  }

  async function removeWxAccount(wxid: string) {
    if (!assertOwnWechatAccount(wxid)) return
    if (
      !(await dialog.confirm({
        title: '确认下线并移除微信账号？',
        message: `wxid：${wxid}\n\n此操作会先请求协议退出，再从当前控制台账号列表移除。`,
        danger: true,
      }))
    )
      return
    let succeeded = false
    try {
      await apiPost(`/accounts/${encodeURIComponent(wxid)}/logout-protocol`, {}, { silent: true })
      succeeded = true
    } catch (error) {
      message.error(`下线并移除失败：${reason(error)}`)
    }
    await refreshLocalAccounts()
    if (succeeded && currentWxid === wxid) onCurrentAccountCleared()
  }

  return {
    localAccounts,
    localAccountsLoaded,
    localAccountsError,
    refreshLocalAccounts,
    assertOwnWechatAccount,
    onlineWxAccount,
    downlineWxAccount,
    removeWxAccount,
  }
}
